import type { ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import TaxbyLogo from "@/components/TaxbyLogo";
import { DEFAULT_SEO, pageSeoConfig } from "@/lib/seo";
import { usePageSeo } from "@/hooks/usePageSeo";

interface LegalPageLayoutProps {
  title: string;
  children: ReactNode;
}

const BackToHome = ({ className = "" }: { className?: string }) => (
  <Link
    to="/"
    className={`group inline-flex items-center gap-2.5 rounded-full border border-taxby-purple/20 bg-taxby-purple/5 px-4 py-2.5 text-sm font-medium text-taxby-violet shadow-sm transition-all hover:border-taxby-purple/30 hover:bg-taxby-purple/10 hover:shadow ${className}`}
  >
    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-taxby-purple/15 transition-transform group-hover:-translate-x-0.5">
      <ArrowLeft className="h-4 w-4 text-taxby-purple" aria-hidden />
    </span>
    Volver al inicio
  </Link>
);

const LegalPageLayout = ({ title, children }: LegalPageLayoutProps) => {
  const { pathname } = useLocation();
  const seo =
    pageSeoConfig[pathname] ?? {
      title: `${title} | Taxby`,
      description: DEFAULT_SEO.description,
      path: pathname,
    };
  usePageSeo(seo);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/80 via-gray-50 to-white">
      <header className="sticky top-0 z-10 border-b border-purple-100/80 bg-white/95 shadow-sm backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <TaxbyLogo variant="dark" />
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 md:py-12">
        <div className="mx-auto max-w-4xl rounded-2xl border border-purple-100/80 bg-white p-6 shadow-sm md:p-10">
          <BackToHome className="mb-8" />
          <h1 className="mb-8 text-3xl font-bold text-gray-900 md:text-4xl">{title}</h1>
          <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-a:text-taxby-purple">
            {children}
          </div>
          <div className="mt-12 border-t border-gray-100 pt-8">
            <BackToHome />
          </div>
        </div>
      </main>
    </div>
  );
};

export default LegalPageLayout;
